// src/modules/dl/BackpropLab.jsx
// Backprop Lab — VISUAL neural network simulator
// Every neuron drawn. Every weight colored. Watch gradients flow backward live.

import React, { useState, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--cyan)';
const P  = ['#22d3ee','#4ade80','#a78bfa','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,transition:'all .12s',...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?4:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',borderRadius:'2px',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ENGINE ─────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

const ACTS = {
  relu:    { fn: x=>Math.max(0,x),              d: x=>x>0?1:0 },
  sigmoid: { fn: x=>1/(1+Math.exp(-x)),         d: x=>{ const s=1/(1+Math.exp(-x)); return s*(1-s); } },
  tanh:    { fn: x=>Math.tanh(x),               d: x=>1-Math.tanh(x)**2 },
  leaky:   { fn: x=>x>0?x:0.01*x,               d: x=>x>0?1:0.01 },
};

const initNet = (arch, initType='he') =>
  arch.slice(0,-1).map((fi,l) => {
    const fo = arch[l+1];
    const s = initType==='he'?Math.sqrt(2/fi):initType==='xavier'?Math.sqrt(2/(fi+fo)):initType==='zeros'?0:0.1;
    return { W: Array.from({length:fo},()=>Array.from({length:fi},()=>initType==='zeros'?0:randn()*s)), b: Array.from({length:fo},()=>0) };
  });

const forward = (x, layers, act) => {
  const {fn} = ACTS[act];
  let a = [...x];
  const cache = [{ a:[...x], z:[...x] }];
  layers.forEach((layer,l) => {
    const z = layer.W.map((row,i)=>row.reduce((s,w,j)=>s+w*a[j],0)+layer.b[i]);
    a = l===layers.length-1 ? z.map(v=>1/(1+Math.exp(-v))) : z.map(fn);
    cache.push({ a:[...a], z:[...z] });
  });
  return { out:a, cache };
};

const backward = (cache, layers, y, lr, act) => {
  const {d} = ACTS[act];
  const L = layers.length;
  const newL = layers.map(l=>({ W:l.W.map(r=>[...r]), b:[...l.b] }));
  const gL = layers.map(l=>({ dW:l.W.map(r=>r.map(()=>0)), db:l.b.map(()=>0), norm:0 }));
  let delta = cache[L].a.map((a,i)=>a-y[i]);
  for (let l=L-1;l>=0;l--) {
    const ap = cache[l].a;
    let n2 = 0;
    for (let i=0;i<newL[l].W.length;i++) {
      for (let j=0;j<newL[l].W[i].length;j++) {
        const g = delta[i]*ap[j];
        gL[l].dW[i][j]=g; n2+=g*g;
        newL[l].W[i][j]-=lr*g;
      }
      gL[l].db[i]=delta[i];
      newL[l].b[i]-=lr*delta[i];
    }
    gL[l].norm = Math.sqrt(n2);
    if (l>0) {
      const da = cache[l].z.map(d);
      delta = layers[l].W[0].map((_,j)=>layers[l].W.reduce((s,row,i)=>s+row[j]*delta[i],0)*da[j]);
    }
  }
  return { layers:newL, gradLayers:gL };
};

// ─── SVG NETWORK DIAGRAM ─────────────────────────────────────────────────────
function NetSVG({ arch, layerData, gradLayers, mode, selectedLayer, onSelectLayer }) {
  const VW=700, VH=380;
  const layerX = arch.map((_,l)=>60+l*((VW-120)/(Math.max(arch.length-1,1))));
  const R = Math.min(20, Math.max(7, 170/Math.max(...arch)));
  const neuronY = (l,i) => { const n=arch[l]; const sp=Math.min(52,(VH-80)/Math.max(n,1)); const tot=(n-1)*sp; return VH/2-tot/2+i*sp; };

  const allW = layerData ? layerData.slice(1).flatMap(l=>l.W?.flat()||[]) : [];
  const maxW = Math.max(0.01,...allW.map(Math.abs));
  const allG = gradLayers ? gradLayers.flatMap(g=>g.dW?.flat()||[]) : [];
  const maxG = Math.max(0.0001,...allG.map(Math.abs));

  const wColor = (w) => { const t=Math.max(-1,Math.min(1,w/maxW)); return t>0?`rgba(74,222,128,${0.1+t*0.9})`:`rgba(248,113,113,${0.1+(-t)*0.9})`; };
  const gColor = (g) => { const v=Math.min(1,Math.abs(g)/maxG); return `rgba(251,191,36,${0.05+v*0.95})`; };
  const aColor = (a) => { const v=Math.max(0,Math.min(1,Math.abs(a))); return `rgba(34,211,238,${0.08+v*0.92})`; };

  return (
    <svg width="100%" viewBox={`0 0 ${VW} ${VH}`} style={{background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b',display:'block'}}>
      {arch.slice(0,-1).map((_,l) => {
        const srcW = mode==='gradients' ? gradLayers?.[l]?.dW : layerData?.[l+1]?.W;
        return Array.from({length:arch[l]},(_,j)=>
          Array.from({length:arch[l+1]},(_,i)=>{
            const w = srcW?.[i]?.[j] ?? 0;
            const col = mode==='gradients' ? gColor(w) : wColor(w);
            const thick = 0.3 + Math.min(3, Math.abs(w)/(mode==='gradients'?maxG:maxW)*3);
            const dim = selectedLayer!==null && selectedLayer!==l && selectedLayer!==l+1;
            return <line key={`${l}-${j}-${i}`} x1={layerX[l]} y1={neuronY(l,j)} x2={layerX[l+1]} y2={neuronY(l+1,i)}
              stroke={col} strokeWidth={thick} opacity={dim?0.1:0.85}/>;
          })
        );
      })}
      {arch.map((n,l)=>
        Array.from({length:n},(_,i)=>{
          const act = layerData?.[l]?.a?.[i] ?? 0;
          const fill = mode==='gradients' && l>0 && gradLayers?.[l-1]
            ? gColor(gradLayers[l-1].norm/Math.max(1,arch[l]))
            : aColor(act);
          const isSel = selectedLayer===l;
          return (
            <g key={`n${l}-${i}`} onClick={()=>onSelectLayer?.(l)} style={{cursor:'pointer'}}>
              <circle cx={layerX[l]} cy={neuronY(l,i)} r={R} fill={fill} stroke={isSel?'#fbbf24':'#334155'} strokeWidth={isSel?2.5:1}/>
              {R>=11&&<text x={layerX[l]} y={neuronY(l,i)+0.5} textAnchor="middle" dominantBaseline="middle"
                style={{fontSize:`${Math.max(6,R*0.5)}px`,fill:'#f1f5f9',fontFamily:'JetBrains Mono',fontWeight:600,pointerEvents:'none'}}>
                {act.toFixed(R>=16?2:1)}
              </text>}
            </g>
          );
        })
      )}
      {arch.map((_,l)=>(
        <text key={`lbl${l}`} x={layerX[l]} y={VH-10} textAnchor="middle"
          style={{fontSize:'9px',fill:selectedLayer===l?'#fbbf24':'#475569',fontFamily:'JetBrains Mono',fontWeight:selectedLayer===l?700:400}}>
          {l===0?'INPUT':l===arch.length-1?'OUTPUT':`H${l}`} ({arch[l]})
        </text>
      ))}
    </svg>
  );
}

// ─── WEIGHT HEATMAP ──────────────────────────────────────────────────────────
function WeightHeat({ W, title }) {
  if (!W?.length) return null;
  const nO=W.length, nI=W[0]?.length||0;
  const max = Math.max(0.01,...W.flat().map(Math.abs));
  const cW = Math.min(32,Math.max(8,Math.floor(420/nI)));
  const cH = Math.min(32,Math.max(8,Math.floor(220/nO)));
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.08em'}}>{title} — {nO}×{nI}</div>
      <div style={{overflowX:'auto',border:'1px solid #1e293b',borderRadius:'4px',display:'inline-block',maxWidth:'100%'}}>
        {W.map((row,i)=>(
          <div key={i} style={{display:'flex'}}>
            {row.map((w,j)=>{
              const t=w/max;
              const bg=t>0?`rgba(74,222,128,${0.07+Math.abs(t)*0.93})`:`rgba(248,113,113,${0.07+Math.abs(t)*0.93})`;
              return <div key={j} title={`[${i}][${j}] = ${w.toFixed(4)}`}
                style={{width:cW,height:cH,background:bg,border:'1px solid #060b14',boxSizing:'border-box',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:`${Math.min(8,cW*0.28)}px`,color:Math.abs(t)>0.5?'#000':'#94a3b8',fontFamily:'JetBrains Mono',flexShrink:0}}>
                {cW>16&&cH>13?w.toFixed(1):''}
              </div>;
            })}
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:'14px',marginTop:'5px',fontSize:'9px',color:'var(--t3)'}}>
        <span><span style={{color:'#4ade80'}}>█</span> positive</span>
        <span><span style={{color:'#f87171'}}>█</span> negative</span>
        <span>max |w| = {max.toFixed(4)}</span>
      </div>
    </div>
  );
}

// ─── GRADIENT HEATMAP ────────────────────────────────────────────────────────
function GradHeat({ dW, title }) {
  if (!dW?.length) return null;
  const nO=dW.length, nI=dW[0]?.length||0;
  const max=Math.max(1e-10,...dW.flat().map(Math.abs));
  const cW=Math.min(32,Math.max(8,Math.floor(420/nI)));
  const cH=Math.min(32,Math.max(8,Math.floor(220/nO)));
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px',letterSpacing:'.08em'}}>{title} ∂L/∂W — {nO}×{nI}</div>
      <div style={{overflowX:'auto',border:'1px solid #1e293b',borderRadius:'4px',display:'inline-block',maxWidth:'100%'}}>
        {dW.map((row,i)=>(
          <div key={i} style={{display:'flex'}}>
            {row.map((g,j)=>{
              const v=Math.abs(g)/max;
              const bg=`rgba(251,191,36,${0.04+v*0.96})`;
              return <div key={j} title={`∂L/∂W[${i}][${j}] = ${g.toFixed(6)}`}
                style={{width:cW,height:cH,background:bg,border:'1px solid #060b14',boxSizing:'border-box',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:`${Math.min(8,cW*0.28)}px`,color:v>0.5?'#000':'#94a3b8',fontFamily:'JetBrains Mono',flexShrink:0}}>
                {cW>16&&cH>13?g.toFixed(2):''}
              </div>;
            })}
          </div>
        ))}
      </div>
      <div style={{display:'flex',gap:'14px',marginTop:'5px',fontSize:'9px',color:'var(--t3)'}}>
        <span><span style={{color:'#fbbf24'}}>█</span> large gradient</span>
        <span>max = {max.toFixed(6)}</span>
      </div>
    </div>
  );
}

// ─── ACTIVATION DIST ─────────────────────────────────────────────────────────
function ActDist({ acts, label, color='#22d3ee' }) {
  if (!acts?.length) return null;
  const bins=14, mn=Math.min(...acts), mx=Math.max(...acts), rng=mx-mn||0.001;
  const hist=Array(bins).fill(0);
  acts.forEach(v=>{const b=Math.min(bins-1,Math.floor((v-mn)/rng*bins));hist[b]++;});
  const data=hist.map((c,i)=>({x:(mn+i*rng/bins).toFixed(2),c}));
  const μ=(acts.reduce((s,v)=>s+v,0)/acts.length).toFixed(3);
  const σ=Math.sqrt(acts.reduce((s,v)=>s+(v-Number(μ))**2,0)/acts.length).toFixed(3);
  const dead=acts.filter(v=>Math.abs(v)<0.001).length;
  return (
    <div>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'3px'}}>{label}</div>
      <ResponsiveContainer width="100%" height={80}>
        <BarChart data={data} margin={{top:2,bottom:0,left:0,right:0}}>
          <XAxis dataKey="x" tick={{fontSize:7,fill:'#475569'}} interval={Math.floor(bins/4)}/>
          <YAxis tick={{fontSize:7,fill:'#475569'}} width={18}/>
          <Bar dataKey="c" fill={color} opacity={0.85} radius={[1,1,0,0]}/>
        </BarChart>
      </ResponsiveContainer>
      <div style={{display:'flex',gap:'10px',fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>
        <span>μ={μ}</span><span>σ={σ}</span>
        <span style={{color:dead/acts.length>0.5?'#f87171':'#64748b'}}>dead={dead}/{acts.length}</span>
      </div>
    </div>
  );
}

// ─── DATASETS ─────────────────────────────────────────────────────────────────
const mkSpiral=()=>{const p=[];for(let c=0;c<2;c++)for(let i=0;i<60;i++){const r=i/60+0.1,t=i/60*4+c*4+randn()*0.25;p.push({x:[r*Math.sin(t),r*Math.cos(t)],y:[c]});}return p;};
const mkCircle=()=>{const p=[];for(let i=0;i<100;i++){const r=Math.random()*2,t=Math.random()*6.28;p.push({x:[r*Math.cos(t),r*Math.sin(t)],y:[r>1?1:0]});}return p;};
const DATASETS={
  xor:    {data:[[0,0],[0,1],[1,0],[1,1]].map((x,i)=>({x,y:[i===1||i===2?1:0]})),name:'XOR (4)'},
  spiral: {data:mkSpiral(),name:'Spiral (120)'},
  circle: {data:mkCircle(),name:'Circle (100)'},
};

const TABS=['network','weights','gradients','training','optimizer-race'];

export default function BackpropLab() {
  const [tab,setTab]=useState('network');
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Hit INIT NETWORK → neurons appear. TRAIN → watch weights change color.',t:'info'}]);
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [dataKey,setDataKey]=useState('xor');
  const [hidden,setHidden]=useState([4,4]);
  const [act,setAct]=useState('relu');
  const [initType,setInitType]=useState('he');
  const [lr,setLr]=useState(0.05);
  const [epochs,setEpochs]=useState(1000);
  const [running,setRunning]=useState(false);
  const [vizMode,setVizMode]=useState('activations');
  const [selLayer,setSelLayer]=useState(null);
  const [sampleIdx,setSampleIdx]=useState(0);

  const [netLayers,setNetLayers]=useState(null);
  const [layerData,setLayerData]=useState(null);
  const [fwdCache,setFwdCache]=useState(null);
  const [gradLayers,setGradLayers]=useState(null);
  const [history,setHistory]=useState([]);

  const [raceLr,setRaceLr]=useState(0.01);
  const [raceResult,setRaceResult]=useState(null);
  const [raceRunning,setRaceRunning]=useState(false);

  const ds=DATASETS[dataKey];
  const arch=[ds.data[0].x.length,...hidden,1];

  const buildLD=(layers,cache)=>layers?[{a:cache[0].a,W:null,b:null},...layers.map((l,i)=>({W:l.W,b:l.b,a:cache[i+1]?.a||[]}))]:null;

  const doFwd=(layers,idx)=>{const s=ds.data[idx%ds.data.length];return forward(s.x,layers,act);};

  const initNet_=()=>{
    const L=initNet(arch,initType);
    const fwd=doFwd(L,sampleIdx);
    setNetLayers(L); setFwdCache(fwd.cache); setLayerData(buildLD(L,fwd.cache));
    setGradLayers(null); setHistory([]);
    log(`Initialized [${arch.join('→')}] init=${initType}  Click any layer to highlight.`,'ok');
  };

  const trainNet=()=>{
    setRunning(true);
    setTimeout(()=>{
      let L=netLayers||initNet(arch,initType);
      const hist=[]; let lastG=null;
      for(let e=0;e<epochs;e++){
        let tl=0;
        for(const {x,y} of ds.data){
          const fwd=forward(x,L,act);
          const bwd=backward(fwd.cache,L,y,lr,act);
          L=bwd.layers; lastG=bwd.gradLayers;
          tl+=fwd.out.reduce((s,p,i)=>s+(p-y[i])**2,0);
        }
        if(e%Math.max(1,Math.floor(epochs/80))===0){
          const pt={epoch:e,loss:+(tl/ds.data.length).toFixed(5)};
          lastG?.forEach((g,i)=>{pt[`L${i+1}`]=+g.norm.toFixed(6);});
          hist.push(pt);
        }
      }
      const fwd=doFwd(L,sampleIdx);
      setNetLayers(L); setFwdCache(fwd.cache); setLayerData(buildLD(L,fwd.cache));
      setGradLayers(lastG); setHistory(hist);
      let acc=0; for(const {x,y} of ds.data){const p=forward(x,L,act).out[0];if(Math.round(p)===y[0])acc++;}
      log(`Done ${epochs} epochs | loss=${hist.at(-1)?.loss} | acc=${(acc/ds.data.length*100).toFixed(0)}%`,'ok');
      setRunning(false);
    },20);
  };

  const stepSample=(idx)=>{
    setSampleIdx(idx);
    if(netLayers){
      const s=ds.data[idx%ds.data.length];
      const fwd=forward(s.x,netLayers,act);
      const bwd=backward(fwd.cache,netLayers,s.y,lr,act);
      setFwdCache(fwd.cache); setLayerData(buildLD(netLayers,fwd.cache)); setGradLayers(bwd.gradLayers);
    }
  };

  const runRace=()=>{
    setRaceRunning(true);
    setTimeout(()=>{
      const data=DATASETS.spiral.data; const rArch=[2,6,6,1]; const results={};
      for(const name of ['sgd','momentum','adam','rmsprop']){
        let L=initNet(rArch,'xavier');
        let mt=L.map(l=>({mW:l.W.map(r=>r.map(()=>0)),vW:l.W.map(r=>r.map(()=>0))}));
        let t=0; const losses=[];
        for(let e=0;e<400;e++){
          let tl=0;
          for(const {x,y} of data){
            const fwd=forward(x,L,'relu');
            const bwd=backward(fwd.cache,L,y,0,'relu');
            t++;
            L=L.map((layer,li)=>{
              const g=bwd.gradLayers[li];
              const nW=layer.W.map((row,i)=>row.map((w,j)=>{
                const gw=g.dW[i][j];
                if(name==='sgd') return w-raceLr*gw;
                if(name==='momentum'){mt[li].mW[i][j]=0.9*mt[li].mW[i][j]+gw;return w-raceLr*mt[li].mW[i][j];}
                if(name==='rmsprop'){mt[li].vW[i][j]=0.9*mt[li].vW[i][j]+0.1*gw*gw;return w-raceLr*gw/(Math.sqrt(mt[li].vW[i][j])+1e-8);}
                mt[li].mW[i][j]=0.9*mt[li].mW[i][j]+0.1*gw;
                mt[li].vW[i][j]=0.999*mt[li].vW[i][j]+0.001*gw*gw;
                const mh=mt[li].mW[i][j]/(1-0.9**t),vh=mt[li].vW[i][j]/(1-0.999**t);
                return w-raceLr*mh/(Math.sqrt(vh)+1e-8);
              }));
              return{...layer,W:nW};
            });
            tl+=fwd.out.reduce((s,p,i)=>s+(p-y[i])**2,0);
          }
          if(e%20===0) losses.push({epoch:e,loss:+(tl/data.length).toFixed(5)});
        }
        results[name]=losses;
      }
      const chart=results.sgd.map((p,i)=>({epoch:p.epoch,SGD:results.sgd[i]?.loss,Momentum:results.momentum[i]?.loss,Adam:results.adam[i]?.loss,RMSProp:results.rmsprop[i]?.loss}));
      const finals={};['sgd','momentum','adam','rmsprop'].forEach(k=>{finals[k]=results[k].at(-1)?.loss;});
      setRaceResult({chart,finals});
      log(`Race done lr=${raceLr} | Adam=${finals.adam?.toFixed(5)} SGD=${finals.sgd?.toFixed(5)}`,'ok');
      setRaceRunning(false);
    },20);
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* Controls */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sel label="DATASET" value={dataKey} onChange={v=>{setDataKey(v);setNetLayers(null);setLayerData(null);setGradLayers(null);}} options={Object.entries(DATASETS).map(([k,v])=>({value:k,label:v.name}))}/>
        <Sel label="ACTIVATION" value={act} onChange={setAct} options={['relu','sigmoid','tanh','leaky']}/>
        <Sel label="WEIGHT INIT" value={initType} onChange={setInitType} options={['he','xavier','zeros','random']}/>
        <div>
          <Sl label="Learning Rate" value={lr} min={0.001} max={0.5} step={0.001} onChange={setLr} color="#4ade80"/>
          <Sl label="Epochs" value={epochs} min={100} max={3000} step={100} onChange={setEpochs} color={AC}/>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
          <Btn onClick={initNet_} color="#a78bfa">INIT</Btn>
          <Btn onClick={trainNet} disabled={running||!netLayers} color="#4ade80">{running?'TRAINING…':'TRAIN →'}</Btn>
        </div>
      </div>

      {/* ── NETWORK TAB ── */}
      {tab==='network'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>VIEW:</span>
            {['activations','weights','gradients'].map(m=>(
              <button key={m} onClick={()=>setVizMode(m)} style={{padding:'4px 12px',border:`1px solid ${vizMode===m?AC:'var(--b0)'}`,background:vizMode===m?AC+'18':'transparent',color:vizMode===m?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{m}</button>
            ))}
            <span style={{fontSize:'9px',color:'var(--t3)',marginLeft:'14px',letterSpacing:'.1em'}}>SAMPLE:</span>
            {ds.data.slice(0,Math.min(8,ds.data.length)).map((_,i)=>(
              <button key={i} onClick={()=>stepSample(i)} style={{padding:'4px 10px',border:`1px solid ${sampleIdx===i?'#fbbf24':'var(--b0)'}`,background:sampleIdx===i?'#fbbf2415':'transparent',color:sampleIdx===i?'#fbbf24':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                [{ds.data[i]?.x.join(',')}]
              </button>
            ))}
          </div>

          <Panel title={`NETWORK — ${vizMode.toUpperCase()}`} accent={vizMode==='gradients'?'var(--gold)':vizMode==='weights'?'var(--green)':AC}>
            {layerData
              ? <NetSVG arch={arch} layerData={layerData} gradLayers={gradLayers} mode={vizMode} selectedLayer={selLayer} onSelectLayer={l=>setSelLayer(s=>s===l?null:l)}/>
              : <div style={{height:380,display:'flex',alignItems:'center',justifyContent:'center',color:'var(--t3)',fontSize:'12px',background:'#060b14',borderRadius:'8px',border:'1px solid #1e293b'}}>Hit INIT → neurons appear · TRAIN → colors show learned weights</div>
            }
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'6px',fontSize:'10px'}}>
            {[['● Bright neuron','High activation value',AC],['— Green line','Positive weight','#4ade80'],['— Red line','Negative weight · — Yellow = large gradient','#f87171']].map(([icon,text,c])=>(
              <div key={icon} style={{padding:'8px 10px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'3px',display:'flex',gap:'8px',alignItems:'center'}}>
                <span style={{color:c,flexShrink:0}}>{icon.split(' ')[0]}</span>
                <span style={{color:'var(--t3)',fontSize:'9px'}}>{text}</span>
              </div>
            ))}
          </div>

          {fwdCache&&(
            <div style={{display:'grid',gridTemplateColumns:`repeat(${Math.min(arch.length,5)},1fr)`,gap:'8px'}}>
              {fwdCache.map((lc,l)=>(
                <Panel key={l} title={l===0?'INPUT':l===arch.length-1?'OUTPUT':`HIDDEN ${l}`}
                  accent={l===0?AC:l===arch.length-1?'#4ade80':'#a78bfa'}
                  style={{cursor:'pointer',outline:selLayer===l?'2px solid #fbbf24':'none'}}
                  onClick={()=>setSelLayer(s=>s===l?null:l)}>
                  <ActDist acts={lc.a} label={`n=${lc.a.length}`} color={l===0?'#22d3ee':l===arch.length-1?'#4ade80':'#a78bfa'}/>
                </Panel>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── WEIGHTS TAB ── */}
      {tab==='weights'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {layerData&&<Panel title="NETWORK — WEIGHTS VIEW" accent="var(--green)">
            <NetSVG arch={arch} layerData={layerData} gradLayers={gradLayers} mode="weights" selectedLayer={selLayer} onSelectLayer={l=>setSelLayer(s=>s===l?null:l)}/>
          </Panel>}
          {netLayers
            ? netLayers.map((layer,li)=>(
                <Panel key={li} title={`W${li+1}  (${layer.W.length} neurons out × ${layer.W[0]?.length} in)`}
                  accent={selLayer===li+1?'#fbbf24':'var(--green)'} style={{cursor:'pointer'}} onClick={()=>setSelLayer(s=>s===li+1?null:li+1)}>
                  <WeightHeat W={layer.W} title="WEIGHT MATRIX"/>
                  {(()=>{const f=layer.W.flat();const μ=f.reduce((s,v)=>s+v,0)/f.length;const σ=Math.sqrt(f.reduce((s,v)=>s+(v-μ)**2,0)/f.length);const mx=Math.max(...f.map(Math.abs));const sp=f.filter(v=>Math.abs(v)<0.01).length;return(
                    <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',marginTop:'8px'}}>
                      {[['mean',μ.toFixed(4),'#22d3ee'],['std σ',σ.toFixed(4),'#a78bfa'],['max |w|',mx.toFixed(4),'#4ade80'],['near-zero',`${sp}/${f.length}`,sp/f.length>0.5?'#f87171':'#64748b']].map(([k,v,c])=>(
                        <div key={k} style={{background:'var(--deep)',padding:'6px 8px',borderRadius:'2px'}}>
                          <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>{k}</div>
                          <div style={{fontSize:'11px',color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</div>
                        </div>
                      ))}
                    </div>
                  );})()}
                </Panel>
              ))
            : <div style={{padding:'60px',textAlign:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Init the network first</div>
          }
        </div>
      )}

      {/* ── GRADIENTS TAB ── */}
      {tab==='gradients'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {layerData&&<Panel title="GRADIENT FLOW — YELLOW = LARGE ∂L/∂W" accent="var(--gold)">
            <NetSVG arch={arch} layerData={layerData} gradLayers={gradLayers} mode="gradients" selectedLayer={selLayer} onSelectLayer={l=>setSelLayer(s=>s===l?null:l)}/>
          </Panel>}
          <div style={{display:'flex',gap:'8px',alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>STEP THROUGH SAMPLES:</span>
            {ds.data.slice(0,Math.min(8,ds.data.length)).map((s,i)=>(
              <button key={i} onClick={()=>stepSample(i)} style={{padding:'4px 10px',border:`1px solid ${sampleIdx===i?'#fbbf24':'var(--b0)'}`,background:sampleIdx===i?'#fbbf2415':'transparent',color:sampleIdx===i?'#fbbf24':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                x=[{s.x.join(',')}] → {s.y[0]}
              </button>
            ))}
          </div>
          {gradLayers
            ? <>
                <Panel title="GRADIENT NORM PER LAYER" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={160}>
                    <BarChart data={gradLayers.map((g,i)=>({layer:`L${i+1}`,norm:+g.norm.toFixed(6)}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="layer" tick={{fontSize:10,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:10,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Bar dataKey="norm" name="‖∇W‖" radius={[3,3,0,0]}>
                        {gradLayers.map((g,i)=><Cell key={i} fill={g.norm<1e-4?'#f87171':g.norm<0.01?'#fbbf24':'#4ade80'} opacity={.9}/>)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Panel>
                {gradLayers.map((gl,li)=>(
                  <Panel key={li} title={`LAYER ${li+1} GRADIENTS ∂L/∂W${li+1}`} accent="var(--gold)">
                    <GradHeat dW={gl.dW} title={`Layer ${li+1}`}/>
                    {(()=>{const f=gl.dW.flat();const mx=Math.max(...f.map(Math.abs));const van=f.filter(v=>Math.abs(v)<1e-5).length;return(
                      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'6px',marginTop:'8px'}}>
                        {[['‖∇W‖',gl.norm.toFixed(6),'#fbbf24'],['max |∂L/∂W|',mx.toFixed(6),'#fb923c'],['vanishing',`${van}/${f.length}`,van/f.length>0.5?'#f87171':'#4ade80'],['status',gl.norm<1e-4?'VANISH':gl.norm>10?'EXPLODE':'OK',gl.norm<1e-4?'#f87171':gl.norm>10?'#fb923c':'#4ade80']].map(([k,v,c])=>(
                          <div key={k} style={{background:'var(--deep)',padding:'6px 8px',borderRadius:'2px'}}>
                            <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>{k}</div>
                            <div style={{fontSize:'10px',color:c,fontFamily:'JetBrains Mono',fontWeight:700}}>{v}</div>
                          </div>
                        ))}
                      </div>
                    );})()}
                  </Panel>
                ))}
              </>
            : <div style={{padding:'60px',textAlign:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Train or step through a sample</div>
          }
          <LogPanel logs={logs} height={90}/>
        </div>
      )}

      {/* ── TRAINING TAB ── */}
      {tab==='training'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {history.length>0
            ? <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                  <Metric label="Final Loss" value={history.at(-1)?.loss} color="var(--red)"/>
                  <Metric label="Epochs" value={epochs} color={AC}/>
                  <Metric label="Architecture" value={`[${arch.join('→')}]`} color="var(--violet)"/>
                  <Metric label="Dataset" value={ds.name} color="var(--gold)"/>
                </div>
                <Panel title="LOSS CURVE" accent="var(--red)">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="loss" stroke="#f87171" strokeWidth={2} dot={false}/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="GRADIENT NORMS — EACH LAYER OVER TRAINING" accent="var(--gold)">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                      <Tooltip/>
                      <Legend/>
                      {Object.keys(history[0]||{}).filter(k=>k.startsWith('L')).map((k,i)=>(
                        <Line key={k} type="monotone" dataKey={k} stroke={P[i%P.length]} strokeWidth={1.5} dot={false}/>
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
              </>
            : <div style={{padding:'60px',textAlign:'center',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Train to see curves</div>
          }
          <LogPanel logs={logs} height={110}/>
        </div>
      )}

      {/* ── OPTIMIZER RACE TAB ── */}
      {tab==='optimizer-race'&&(
        <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px'}}>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            <Panel title="RACE SETUP" accent="var(--gold)">
              <Sl label="Learning Rate" value={raceLr} min={0.0001} max={0.1} step={0.0001} onChange={setRaceLr} color="#fbbf24"/>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',lineHeight:1.5}}>SGD · Momentum · Adam · RMSProp — same net, same data, same LR.</div>
              <Btn onClick={runRace} disabled={raceRunning} color="#fbbf24" style={{width:'100%'}}>{raceRunning?'RACING…':'START →'}</Btn>
            </Panel>
            <LogPanel logs={logs} height={160}/>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {raceResult
              ? <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                    {Object.entries(raceResult.finals).map(([k,v],i)=><Metric key={k} label={k.toUpperCase()} value={v?.toFixed(5)||'N/A'} color={P[i]}/>)}
                  </div>
                  <Panel title="LOSS RACE" accent="var(--gold)">
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={raceResult.chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                        <XAxis dataKey="epoch" tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                        <Tooltip/>
                        <Legend/>
                        {['SGD','Momentum','Adam','RMSProp'].map((k,i)=><Line key={k} type="monotone" dataKey={k} stroke={P[i]} strokeWidth={2} dot={false} connectNulls/>)}
                      </LineChart>
                    </ResponsiveContainer>
                  </Panel>
                  <div style={{display:'flex',gap:'8px'}}>
                    {Object.entries(raceResult.finals).sort((a,b)=>a[1]-b[1]).map(([k,v],rank)=>(
                      <div key={k} style={{flex:1,padding:'14px',background:'var(--raised)',border:`1px solid ${P[rank]}44`,borderRadius:'3px',textAlign:'center'}}>
                        <div style={{fontFamily:'Syne',fontWeight:800,fontSize:'22px',color:P[rank]}}>{rank+1}</div>
                        <div style={{fontSize:'11px',color:'var(--t1)',fontWeight:600}}>{k}</div>
                        <div style={{fontSize:'10px',color:P[rank],fontFamily:'JetBrains Mono'}}>{v?.toFixed(5)}</div>
                      </div>
                    ))}
                  </div>
                </>
              : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Set LR, start the race</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}
